import React, {
  FunctionComponent,
  ReactElement,
  useState,
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
} from 'react';
import styled from 'styled-components';
import { useMedia } from 'react-media-match';
import { useIntl, defineMessages, FormattedMessage } from 'react-intl';
import CountUp from 'react-countup';
import InputRange from 'react-input-range';
import { CircleSlider } from 'react-circle-slider-hooked';
import get from 'lodash.get';
import { formatCurrency } from '@empiriecom/module-components/FormattedCurrency';
import Notification from '@empiriecom/module-components/Notification';
import useTheme from '@empiriecom/module-components/hooks/useTheme';
import getIcon from '@empiriecom/module-components/utils/getIcon';
import useConfig from '@empiriecom/module-components/hooks/useConfig';
import { InfoIcon } from '@/src/components/icons';
import { FragmentConfiguration } from '@/config';
import OverviewTable from '../../InstallmentCalculatorGlobals/OverviewTable';
import Reveal, { RevealEvent } from './Reveal';
import InstallmentPriceInput from '../../InstallmentCalculatorGlobals/PriceInput';
import InstallmentCalculatorFooter from '../../InstallmentCalculatorGlobals/Footer';
import { useInstallmentCalculatorContext } from '../../InstallmentCalculatorContext';

const messages = defineMessages({
  cardHeadline: {
    defaultMessage: 'Ratenrechner',
    id: 'installment-calculator.card-headline',
  },
  collapseLabel: {
    defaultMessage: 'Ratenrechner minimieren',
    id: 'installment-calculator.collapse-label',
  },
  expandLabel: {
    defaultMessage: 'Ratenrechner anzeigen',
    id: 'installment-calculator.expand-label',
  },
  sliderLabel: {
    defaultMessage: '{value} Monate',
    id: 'installment-calculator.range-min-max-label-text',
  },
});

const CalculatorContainer = styled.div<{ cardLayout: boolean }>`
  position: relative;
  ${({ cardLayout }) => cardLayout && 'min-height: 6rem;'}
  ${({ cardLayout }) => !cardLayout && 'padding: 2rem;'}
`;

const ModalFlexWrapper = styled.div<{ cardLayout: boolean; mobile: boolean }>`
  ${({ cardLayout, theme }) =>
    !cardLayout &&
    `display:flex;flex-flow:row wrap;justify-content:space-between;align-items:flex-start;border-bottom: 1px solid ${theme.greyNormal.color.normal};`}
  ${({ cardLayout, mobile }) =>
    !cardLayout && !mobile
      ? '>div{width:47%;padding: 0 0.5rem;}'
      : '>div{width:100%;padding: 0 0.5rem;}'}
  ${({ cardLayout }) => !cardLayout && '>div:first-child{overflow:hidden;}'}
`;

const CalculatorPriceAndRate = styled.div`
  background-color: ${({ theme }) => theme.greyLight.color.normal};
  padding: 0.938rem 0;
`;

const CalculatorPrice = styled.div`
  display: flex;
  flex-flow: row wrap;
`;

const CalculatorRate = styled.div`
  display: flex;
  flex-flow: row wrap;
`;

const CalculatorLabel = styled.span`
  width: 100%;
  text-align: center;
  font-size: 0.938rem;
`;

const CalculatorValue = styled.span`
  width: 100%;
  text-align: center;
  font-size: 1.5rem;
`;

const InstallmentInfoWrapper = styled.div<{ isInfoOpen: boolean; wrapperHeight: number }>`
  position: relative;
  .reveal-overlay {
    height: ${({ isInfoOpen, wrapperHeight }) =>
      isInfoOpen ? `calc(${wrapperHeight}px + 0.5rem)` : 'auto'};
  }
  .notification-icon {
    display: none;
  }
`;

const InstallmentInfoIconWrapper = styled.div`
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  align-self: flex-start;
  border: 1px solid transparent;
  margin: 0.5rem 0;
  padding: 0.25rem 0.5rem;
  width: 2rem;
  min-width: 2rem;
  height: 2rem;
  min-height: 2rem;
  position: relative;
  z-index: 2;
`;

const StyledInfoIcon = styled(InfoIcon)`
  width: 1.5rem;
  min-width: 1.5rem;
  height: 1.5rem;
  min-height: 1.5rem;
  vertical-align: middle;
  #a {
    fill: ${({ theme }) => theme.info.color.normal};
  }
  #b {
    fill: #fff;
  }
`;

const StyledCloseIcon = styled(getIcon('Cancel'))`
  min-width: 0.8125rem;
  width: 0.8125rem;
  min-height: 0.8125rem;
  height: 0.8125rem;
  cursor: pointer;
  fill: #262729;
  color: #262729;
`;

const InstallmentInfoCloseWrapper = styled.div<{ isInfoOpen: boolean }>`
  display: ${({ isInfoOpen }) => (isInfoOpen ? 'flex' : 'block')};
  justify-content: flex-end;
  align-items: center;
  align-self: flex-start;
  width: ${({ isInfoOpen }) => (isInfoOpen ? '2rem' : '0')};
  min-width: ${({ isInfoOpen }) => (isInfoOpen ? '2rem' : '0')};
  height: ${({ isInfoOpen }) => (isInfoOpen ? '2rem' : '0')};
  min-height: ${({ isInfoOpen }) => (isInfoOpen ? '2rem' : '0')};
  margin-left: auto;
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  overflow: hidden;
`;

const InstallmentRangeWrapper = styled.div`
  .range-slider-container {
    width: 100%;
    height: 0.25rem;
  }
  .range-track {
    position: relative;
    order: 1;
    height: 0.25rem;
    width: 100%;
    margin-bottom: 0.75rem;
    background-color: ${({ theme }) => theme.greyNormal.color.normal};
    cursor: pointer;
  }
  .range-track-active {
    position: absolute;
    height: 0.25rem;
    background-color: ${({ theme }) => theme.primary.color.normal};
  }
  .range-slider {
    width: 1rem;
    height: 1rem;
    border-radius: 50%;
    overflow: hidden;
    background-color: ${({ theme }) => theme.primary.color.normal};
    position: absolute;
    top: -50%;
    transform: translate(-50%, -25%);
    cursor: pointer;
  }
  .range-input-range,
  .range-disabled-range {
    display: flex;
    flex-flow: row wrap;
  }
  .range-max-label {
    font-size: 0.938rem;
    margin-left: auto;
    order: 3;
    margin-bottom: 1rem;
  }
  .range-min-label {
    font-size: 0.938rem;
    margin-right: auto;
    order: 2;
    margin-bottom: 1rem;
  }
  .range-value-label {
    display: none;
  }
`;

const InstallmentRangeLabel = styled.div<{ mobile: boolean }>`
  margin: 1rem 0;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  ${({ mobile }) => mobile && 'position: absolute; margin: 0;'}
`;

const RangeUnit = styled.div`
  font-size: 1.25rem;
  line-height: normal;
`;

const SelectedRange = styled.div`
  font-size: 2.625rem;
  line-height: normal;
`;

const MobileRangeWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  margin: 2.5rem 0;
`;

/**
 *
 */
export const DynamicInstallmentCalculator: FunctionComponent<{
  setCollapseThreshold: Dispatch<SetStateAction<number>>;
}> = ({ setCollapseThreshold }): ReactElement => {
  const { error, isLoading, response, setNumberOfInstallments } = useInstallmentCalculatorContext();
  const mobile = useMedia({ desktop: false, mobile: true, tablet: false });
  const theme = useTheme();
  const [infoOpen, setInfoOpen] = useState<boolean>(false);
  const [infoHeight, setInfoHeight] = useState<number>(0);
  const [isAnimating, setIsAnimating] = useState<boolean>(false);
  const [installments, setInstallments] = useState<number>();
  const infoWrapperRef = useRef(null);
  const thresholdWrapperRef = useRef(null);
  const intl = useIntl();
  const {
    installmentCalculator: { cardLayout },
  } = useConfig<FragmentConfiguration>();

  useEffect(() => {
    setInfoHeight(get(infoWrapperRef, 'current.clientHeight'));
  });
  useEffect(() => setCollapseThreshold(get(thresholdWrapperRef, 'current.clientHeight')));

  useEffect(() => setInstallments(response.numberOfInstallments), [response]);

  return (
    <>
      {/* render nothing if an error occured. */}
      {(error || (response && response.error)) && (
        <span data-testid="installment-calculator-error" data-error={response && response.error} />
      )}
      {!error && response && !response.error && (
        <CalculatorContainer cardLayout={cardLayout}>
          <div data-testid="rvc-installment-calculator">
            <ModalFlexWrapper cardLayout={cardLayout} mobile={mobile}>
              <div ref={thresholdWrapperRef}>
                <CalculatorPriceAndRate>
                  <CalculatorPrice>
                    <InstallmentPriceInput />
                  </CalculatorPrice>
                  <CalculatorRate>
                    <CalculatorLabel>
                      <FormattedMessage
                        id="installment-calculator.monthly-rate-label"
                        defaultMessage="Monatliche Rate"
                      />
                    </CalculatorLabel>
                    <CalculatorValue>
                      {isLoading ? (
                        <CountUp
                          start={1234}
                          end={9999}
                          delay={0}
                          duration={0.5}
                          onEnd={({ start }) => start()}
                          formattingFn={(number) => formatCurrency(response.currency, number, intl)}
                        >
                          {({ countUpRef }) => (
                            <div>
                              <span ref={countUpRef} />
                            </div>
                          )}
                        </CountUp>
                      ) : (
                        formatCurrency(
                          response.currency,
                          response.singleInstallment.value * 100,
                          intl,
                        )
                      )}
                    </CalculatorValue>
                  </CalculatorRate>
                </CalculatorPriceAndRate>
                <InstallmentInfoWrapper isInfoOpen={infoOpen} wrapperHeight={infoHeight}>
                  <Reveal
                    onChange={(event: RevealEvent) => {
                      if (event.type === 'OPENED' || event.type === 'CLOSED') {
                        setIsAnimating(false);
                      }
                    }}
                    speed="fast"
                    reveal={infoOpen}
                    revealContent={
                      <div ref={infoWrapperRef}>
                        <Notification
                          level="info"
                          isInline
                          content={
                            <FormattedMessage
                              id="installment-calculator.info-text"
                              defaultMessage="Die Ratenhöhe und die Laufzeit liegen in deinen Händen. Durch einfaches Anpassen der Laufzeit findest du deine persönliche Wunschrate."
                            />
                          }
                        />
                        <InstallmentInfoCloseWrapper
                          isInfoOpen={infoOpen}
                          onClick={() => {
                            if (!isAnimating) {
                              setIsAnimating(true);
                              setInfoOpen(!infoOpen);
                            }
                          }}
                        >
                          <StyledCloseIcon />
                        </InstallmentInfoCloseWrapper>
                      </div>
                    }
                  >
                    <InstallmentInfoIconWrapper
                      onClick={() => {
                        if (!isAnimating) {
                          setIsAnimating(true);
                          setInfoOpen(!infoOpen);
                        }
                      }}
                    >
                      <StyledInfoIcon size="1.5rem" />
                    </InstallmentInfoIconWrapper>
                  </Reveal>
                </InstallmentInfoWrapper>
                <InstallmentRangeWrapper>
                  {mobile ? (
                    <MobileRangeWrapper
                      data-testid="rvc-installment-calculator-circle-range"
                      onMouseUp={() => {
                        // eslint-disable-next-line no-restricted-globals
                        blur();
                        if (isLoading || installments === response.numberOfInstallments) {
                          return;
                        }
                        const value = installments;
                        setNumberOfInstallments(value);
                      }}
                      onTouchEnd={() => {
                        if (isLoading || installments === response.numberOfInstallments) {
                          return;
                        }
                        const value = installments;
                        setNumberOfInstallments(value);
                      }}
                    >
                      <InstallmentRangeLabel mobile={!cardLayout || mobile}>
                        <RangeUnit>
                          <FormattedMessage
                            id="installment-calculator.range-unit"
                            defaultMessage="Monate"
                          />
                        </RangeUnit>
                        <SelectedRange>{installments}</SelectedRange>
                      </InstallmentRangeLabel>
                      <CircleSlider
                        circleColor={theme.greyNormal.color.normal}
                        circleWidth={4}
                        progressWidth={4}
                        disabled={isLoading}
                        knobRadius={8}
                        knobColor={theme.primary.color.normal}
                        max={response.viableInstallments[response.viableInstallments.length - 1]}
                        min={response.viableInstallments[0]}
                        onChange={(value) => setInstallments(value)}
                        progressColor={theme.primary.color.normal}
                        shadow={false}
                        size={180}
                        value={installments}
                      />
                    </MobileRangeWrapper>
                  ) : (
                    <div data-testid="rvc-installment-calculator-desktop-range">
                      <InstallmentRangeLabel mobile={mobile}>
                        <RangeUnit>
                          <FormattedMessage
                            id="installment-calculator.range-unit"
                            defaultMessage="Monate"
                          />
                        </RangeUnit>
                        <SelectedRange>{installments}</SelectedRange>
                      </InstallmentRangeLabel>
                      <InputRange
                        classNames={{
                          activeTrack: 'range-track-active',
                          disabledInputRange: 'range-disabled-range',
                          inputRange: 'range-input-range',
                          labelContainer: 'range-label-container',
                          maxLabel: 'range-max-label',
                          minLabel: 'range-min-label',
                          slider: 'range-slider',
                          sliderContainer: 'range-slider-container',
                          track: 'range-track',
                          valueLabel: 'range-value-label',
                        }}
                        disabled={isLoading}
                        formatLabel={(value) => intl.formatMessage(messages.sliderLabel, { value })}
                        maxValue={
                          response.viableInstallments[response.viableInstallments.length - 1]
                        }
                        minValue={response.viableInstallments[0]}
                        onChange={(value) => setInstallments(value as number)}
                        onChangeComplete={(value) => {
                          setInstallments(value as number);
                          setNumberOfInstallments(value as number);
                        }}
                        value={installments}
                      />
                    </div>
                  )}
                </InstallmentRangeWrapper>
              </div>
              <OverviewTable response={response} />
            </ModalFlexWrapper>
            <InstallmentCalculatorFooter />
          </div>
        </CalculatorContainer>
      )}
    </>
  );
};

export default DynamicInstallmentCalculator;
